import type { OrgNode } from "@/types";

/**
 * Check if assigning newManagerPid to userPid would create a circular reference
 * Returns true if valid (no circular reference), false if circular reference detected
 * 
 * @param orgTree - The complete org tree
 * @param userPid - The user being reassigned
 * @param newManagerPid - The proposed new manager
 * @returns true if valid, false if circular reference
 */
export function checkCircularReference(
  orgTree: OrgNode | null,
  userPid: string,
  newManagerPid: string
): boolean {
  if (!orgTree) return true;
  if (userPid === newManagerPid) return false; // Can't report to self

  // Find the new manager node in the tree
  const newManager = findNodeByPid(orgTree, newManagerPid);
  if (!newManager) return true; // New manager not found (shouldn't happen)

  // Walk up from new manager to root, checking if we encounter userPid
  let current: OrgNode | null = newManager;
  const visited = new Set<string>();

  while (current) {
    if (visited.has(current.pid)) {
      // Detect infinite loop in existing tree (data corruption)
      console.error("Infinite loop detected in org tree");
      return false;
    }
    visited.add(current.pid);

    if (current.pid === userPid) {
      // New manager is a subordinate of user - would create circular reference
      return false;
    }

    if (!current.managerPid) {
      // Reached the root (CEO) without finding userPid
      break;
    }

    // Find parent manager
    current = findNodeByPid(orgTree, current.managerPid);
  }

  return true; // No circular reference detected
}

/**
 * Find a node in the org tree by PID
 * Uses depth-first search
 */
export function findNodeByPid(
  node: OrgNode | null,
  pid: string
): OrgNode | null {
  if (!node) return null;
  if (node.pid === pid) return node;

  for (const child of node.directReports) {
    const found = findNodeByPid(child, pid);
    if (found) return found;
  }

  return null;
}

/**
 * Filter org tree to only include visible nodes based on collapsed state
 * Returns a new tree with collapsed branches hidden
 */
export function filterVisibleNodes(
  node: OrgNode,
  collapsedNodes: string[]
): OrgNode {
  if (collapsedNodes.includes(node.pid)) {
    // This node is collapsed, hide its children
    return { ...node, directReports: [] };
  }

  return {
    ...node,
    directReports: node.directReports.map((child) =>
      filterVisibleNodes(child, collapsedNodes)
    ),
  };
}

/**
 * Count total nodes in org tree (including all descendants)
 */
export function countAllNodes(node: OrgNode | null): number {
  if (!node) return 0;
  return 1 + node.directReports.reduce((sum, child) => sum + countAllNodes(child), 0);
}

/**
 * Get maximum depth of org tree
 */
export function getMaxDepth(node: OrgNode | null): number {
  if (!node || node.directReports.length === 0) return 0;
  return 1 + Math.max(...node.directReports.map(getMaxDepth));
}

/**
 * Flatten org tree into array of all nodes
 */
export function flattenOrgTree(node: OrgNode | null): OrgNode[] {
  if (!node) return [];
  return [node, ...node.directReports.flatMap(flattenOrgTree)];
}

/**
 * Search org tree by name (case-insensitive, partial match)
 */
export function searchOrgTree(
  node: OrgNode | null,
  searchQuery: string
): OrgNode[] {
  if (!node) return [];
  
  const query = searchQuery.toLowerCase().trim();
  if (!query) return [];

  const allNodes = flattenOrgTree(node);
  return allNodes.filter((n) =>
    n.fullName.toLowerCase().includes(query) ||
    n.email.toLowerCase().includes(query) ||
    n.position?.toLowerCase().includes(query)
  );
}

/**
 * Filter org tree by department
 */
export function filterByDepartment(
  node: OrgNode | null,
  departments: string[]
): OrgNode[] {
  if (!node || departments.length === 0) return [];

  const allNodes = flattenOrgTree(node);
  return allNodes.filter((n) =>
    n.department && departments.includes(n.department)
  );
}

/**
 * Get the path of PIDs from root to a target node
 * Returns array of PIDs representing the path, or null if node not found
 */
export function getPathToNode(
  tree: OrgNode | null,
  targetPid: string
): string[] | null {
  if (!tree) return null;
  
  function findPath(node: OrgNode, path: string[]): string[] | null {
    const currentPath = [...path, node.pid];
    
    if (node.pid === targetPid) {
      return currentPath;
    }
    
    for (const child of node.directReports) {
      const result = findPath(child, currentPath);
      if (result) return result;
    }
    
    return null;
  }
  
  return findPath(tree, []);
}
